import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Seo } from '../components/Seo';
import { useAuth } from '../context/AuthContext';
import { claimPurchase } from '../lib/api';
import { getStoredAuthToken } from '../lib/authToken';

function safeInternalPath(raw: string | null): string | null {
  if (!raw) return null;
  const s = raw.trim();
  if (!s.startsWith('/') || s.startsWith('//')) return null;
  return s;
}

export function LoginPage() {
  const [params] = useSearchParams();
  const claimSession = params.get('claim_session') ?? '';
  const nextPath = safeInternalPath(params.get('next'));
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const r = await login(email, password);
      if (!r.ok) {
        setErr(r.message);
        return;
      }
      if (claimSession.startsWith('cs_')) {
        const t = getStoredAuthToken();
        if (t) {
          const c = await claimPurchase(t, claimSession);
          if (!c.ok) {
            setErr(c.message ?? c.error);
            return;
          }
        }
      }
      navigate(nextPath ?? '/account', { replace: true });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Seo
        path="/inloggen"
        title="Inloggen"
        description="Log in op je ComfortTool™-account voor aankopen en rapporten."
        keywords="ComfortTool inloggen, account, Mijn ComfortTool"
      />
      <div className="mx-auto max-w-md px-4 py-12">
        <h1 className="text-2xl font-bold text-ct-text">Inloggen</h1>
        {claimSession ? (
          <p className="mt-2 text-sm text-ct-muted">
            Log in met het <strong className="text-ct-text">zelfde e-mailadres</strong> als bij je betaling om je
            aankoop te koppelen.
          </p>
        ) : (
          <p className="mt-2 text-sm text-ct-muted">Toegang tot je aankopen en rapporten.</p>
        )}
        <form
          className="mt-8 space-y-4"
          onSubmit={(e) => void onSubmit(e)}
          aria-describedby={err ? 'login-form-error' : undefined}
        >
          <div>
            <label className="block text-sm font-medium text-ct-text" htmlFor="login-email">
              E-mail
            </label>
            <input
              id="login-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-lg border border-ct-border bg-ct-bg px-3 py-2 text-ct-text"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ct-text" htmlFor="login-password">
              Wachtwoord
            </label>
            <input
              id="login-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-lg border border-ct-border bg-ct-bg px-3 py-2 text-ct-text"
            />
          </div>
          {err ? (
            <p id="login-form-error" className="text-sm text-red-400" role="alert">
              {err}
            </p>
          ) : null}
          <p className="text-right text-sm">
            <Link
              to="/wachtwoord-vergeten"
              className="font-medium text-ct-accent hover:underline"
            >
              Wachtwoord vergeten?
            </Link>
          </p>
          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-xl bg-ct-accent py-2.5 text-sm font-bold text-white hover:bg-blue-500 disabled:opacity-50"
          >
            {busy ? 'Bezig…' : 'Inloggen'}
          </button>
        </form>
        <p className="mt-6 text-center text-sm text-ct-muted">
          Nog geen account?{' '}
          <Link
            to={claimSession ? `/registreren?claim_session=${encodeURIComponent(claimSession)}` : '/registreren'}
            className="font-semibold text-ct-accent hover:underline"
          >
            Registreren
          </Link>
        </p>
      </div>
    </>
  );
}
